import { useCallback, useEffect, useState } from 'react';
import type { AccEinstellung, AccVersionsstand, AccVersionshinweis, ApiClient, ProfilEintrag } from '../../api';
import { ContentSection, DataTable, ErrorState, Feld, LoadingState, StatusBadge, type Zustand } from '../../muster';
import { accFehlertext } from './AccDurchsuchen';
import { zeitpunkt } from './acczeit';
import { GrundDialog, type Rueckfrage } from './AccZuordnung';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * „NEUE VERSIONEN IN ACC“ (AC1d)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Eine neue Version ist zuerst ein Hinweis. Ein Auftrag entsteht nur auf Klick („Aufnehmen …“) oder mit der
 * Einstellung „automatisch aufnehmen“ - und dann nur mit Profilfassung und lesenden Arten. Rückschreiben ins Modell
 * gibt es hier nicht.
 */

/** Die lesenden Aufnahmearten (ExportKind) - die einzigen, die hier wählbar sind. */
export const AUFNAHMEARTEN: { art: number; text: string }[] = [
  { art: 10, text: 'Räume' }, { art: 20, text: 'Türen' }, { art: 30, text: 'Lüftung' }, { art: 32, text: 'Kanalnetz' },
  { art: 40, text: 'Pläne' }, { art: 50, text: 'Heizung' }, { art: 52, text: 'Rohrnetz' }, { art: 60, text: 'Elektro' },
  { art: 70, text: 'Hülle' }, { art: 80, text: 'Sanitär' }, { art: 90, text: 'Außenanlagen' }, { art: 100, text: 'Aussparungen' },
  { art: 110, text: 'Sprinkler' }, { art: 120, text: 'Fremdmodelle' }, { art: 130, text: 'Koordination' }, { art: 140, text: 'Küche' },
];

export function hinweisMarke(status: string): { zustand: Zustand; text: string } {
  switch (status) {
    case 'offen': return { zustand: 'warnung', text: 'neu' };
    case 'auftrag': return { zustand: 'gut', text: 'Auftrag' };
    case 'erledigt': return { zustand: 'neutral', text: 'erledigt' };
    case 'ersetzt': return { zustand: 'neutral', text: 'ersetzt' };
    default: return { zustand: 'neutral', text: status };
  }
}

/** Was fehlt, damit „automatisch aufnehmen“ gespeichert werden darf - wie der Server; `null` wenn nichts. */
export function einstellungFehlt(e: Pick<AccEinstellung, 'versionsabfrage' | 'autoAufnahme' | 'exportProfileVersionId' | 'arten'>): string | null {
  if (!e.autoAufnahme) return null;
  if (!e.versionsabfrage) return '„Automatisch aufnehmen“ braucht die Versionsabfrage.';
  if (!e.exportProfileVersionId) return '„Automatisch aufnehmen“ braucht eine Profilfassung.';
  if (e.arten.length === 0) return '„Automatisch aufnehmen“ braucht mindestens eine Aufnahmeart.';
  return null;
}

export function AccVersionen({ api, projektId, profile = [] }: {
  api: ApiClient; projektId: string; profile?: ProfilEintrag[];
}) {
  const [daten, setDaten] = useState<AccVersionsstand | null>(null);
  const [ladefehler, setLadefehler] = useState<unknown>(null);
  const [form, setForm] = useState<Pick<AccEinstellung, 'versionsabfrage' | 'autoAufnahme' | 'exportProfileVersionId' | 'arten'> | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [rueckfrage, setRueckfrage] = useState<Rueckfrage | null>(null);

  const laden = useCallback(() => api.accVersionsstand(projektId).then((d) => {
    setDaten(d);
    setForm((f) => f ?? {
      versionsabfrage: d.einstellung.versionsabfrage, autoAufnahme: d.einstellung.autoAufnahme,
      exportProfileVersionId: d.einstellung.exportProfileVersionId, arten: d.einstellung.arten,
    });
  }).catch(setLadefehler), [api, projektId]);
  useEffect(() => { laden(); }, [laden]);

  if (ladefehler) return <ContentSection titel="Neue Versionen in ACC"><ErrorState meldung="Die Versionshinweise konnten nicht geladen werden." technisch={ladefehler} /></ContentSection>;
  if (!daten || !form) return <ContentSection titel="Neue Versionen in ACC"><LoadingState zeilen={3} spalten={4} /></ContentSection>;

  const fassungen = profile.flatMap((p) => p.versions.map((v) => ({ wert: v.id, beschriftung: `${p.name} · ${v.version} (Revit ${v.revitVersion})` })));
  const fehlt = einstellungFehlt(form);

  const pruefen = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      const r = await api.accVersionenPruefen(projektId);
      setMeldung(`${r.geprueft} geprüft, ${r.neu.length} neu${r.neu.length ? `: ${r.neu.join(', ')}` : ''}`
        + `${r.fehler.length ? ` - Fehler: ${r.fehler.join('; ')}` : ''}${r.auftragNummer ? ` - Auftrag ${r.auftragNummer}` : ''}`);
      await laden();
    } catch (e) {
      setFehler(accFehlertext(e));
    } finally {
      setLaeuft(false);
    }
  };

  const fertig = async (text: string) => { setRueckfrage(null); setMeldung(text); await laden(); };

  const aufnehmen = (h: AccVersionshinweis) => {
    if (!form.exportProfileVersionId || form.arten.length === 0) {
      setFehler('Für „Aufnehmen“ unten eine Profilfassung und mindestens eine Aufnahmeart wählen.');
      return;
    }
    setRueckfrage({
      titel: `${h.modell ?? 'Modell'} V${h.versionNummer} aufnehmen?`,
      text: `Ein Auftrag mit ${form.arten.map((a) => AUFNAHMEARTEN.find((x) => x.art === a)?.text ?? a).join(', ')} für dieses Modell.`,
      bestaetigen: 'Aufnehmen',
      aktion: async (grund) => {
        const r = await api.accHinweisAufnehmen(projektId, h.id, { exportProfileVersionId: form.exportProfileVersionId!, arten: form.arten, grund });
        await fertig(`Auftrag ${r.auftrag} angelegt.`);
      },
    });
  };

  return (
    <ContentSection
      titel="Neue Versionen in ACC"
      className="acc-versionen-karte"
      beschreibung="Eine neue Version ist zuerst ein Hinweis. Ein Auftrag entsteht nur auf Klick oder mit „automatisch aufnehmen“."
      aktionen={<button className="m-knopf" onClick={pruefen} disabled={laeuft}>Jetzt prüfen</button>}
    >
      {daten.einstellung.letzteAbfrageAm && (
        <p className="sehr-schwach" data-letzte>
          Zuletzt geprüft {zeitpunkt(daten.einstellung.letzteAbfrageAm)}: {daten.einstellung.letzteAbfrageErgebnis}
        </p>
      )}
      {fehler && <ErrorState klein meldung={fehler} />}
      {meldung && <p role="status">{meldung}</p>}

      <div data-hinweise>
        <DataTable<AccVersionshinweis>
          name="Versionshinweise"
          zeilen={daten.hinweise}
          schluessel={(h) => h.id}
          leer="Keine neue Version erkannt."
          spalten={[
            { schluessel: 'modell', titel: 'Modell', wert: (h) => h.modell ?? '' },
            { schluessel: 'version', titel: 'Version', wert: (h) => `V${h.versionNummer}` },
            { schluessel: 'erkannt', titel: 'Erkannt', wert: (h) => zeitpunkt(h.erkanntAm) },
            { schluessel: 'status', titel: 'Status', wert: (h) => h.status, inhalt: (h) => <StatusBadge {...hinweisMarke(h.status)} /> },
            { schluessel: 'auftrag', titel: 'Auftrag / Grund', wert: (h) => h.auftrag ?? h.grund ?? '' },
            {
              schluessel: 'aktion', titel: '',
              inhalt: (h) => (h.status === 'offen' ? (
                <>
                  <button className="m-knopf" onClick={() => aufnehmen(h)}>Aufnehmen …</button>{' '}
                  <button className="m-knopf" onClick={() => setRueckfrage({
                    titel: `${h.modell ?? 'Modell'} V${h.versionNummer} als erledigt vermerken?`,
                    text: 'Kein Auftrag; die Version gilt danach als bekannt.',
                    bestaetigen: 'Erledigt',
                    aktion: async (grund) => { await api.accHinweisErledigen(projektId, h.id, grund); await fertig('Als erledigt vermerkt.'); },
                  })}>Erledigt …</button>
                </>
              ) : null),
            },
          ]}
        />
      </div>

      <fieldset className="acc-einstellung" data-einstellung>
        <legend>Einstellung für dieses Projekt</legend>
        <Feld art="haken" beschriftung="Neue Versionen regelmäßig abfragen" wert={form.versionsabfrage}
              beiAenderung={(w) => setForm({ ...form, versionsabfrage: !!w })} />
        <Feld art="haken" beschriftung="Bei neuer Version automatisch aufnehmen" wert={form.autoAufnahme}
              beiAenderung={(w) => setForm({ ...form, autoAufnahme: !!w })} />
        <Feld art="auswahl" beschriftung="Profilfassung" wert={form.exportProfileVersionId ?? ''} platzhalter="Profilfassung wählen …"
              optionen={fassungen} beiAenderung={(w) => setForm({ ...form, exportProfileVersionId: (w as string) || null })} />
        <div className="acc-arten" role="group" aria-label="Aufnahmearten" data-arten>
          {AUFNAHMEARTEN.map((a) => (
            <label key={a.art}>
              <input type="checkbox" checked={form.arten.includes(a.art)}
                     onChange={(e) => setForm({ ...form, arten: e.target.checked ? [...form.arten, a.art] : form.arten.filter((x) => x !== a.art) })} />
              {' '}{a.text}
            </label>
          ))}
        </div>
        {fehlt && <p data-fehlt><StatusBadge zustand="warnung" text="Fehlt" /> {fehlt}</p>}
        <button className="m-knopf" disabled={fehlt !== null} onClick={() => setRueckfrage({
          titel: 'Einstellung speichern?',
          text: form.autoAufnahme
            ? 'Bei jeder neuen Version entsteht ein Auftrag mit den gewählten Aufnahmearten (nicht in den gesperrten Betriebsfenstern).'
            : 'Neue Versionen werden nur als Hinweis gezeigt.',
          bestaetigen: 'Speichern',
          aktion: async (grund) => { await api.accEinstellungen(projektId, { ...form, grund }); await fertig('Einstellung gespeichert.'); },
        })}>Einstellung speichern …</button>
      </fieldset>

      <GrundDialog rueckfrage={rueckfrage} beiAbbrechen={() => setRueckfrage(null)} />
    </ContentSection>
  );
}
